<script setup lang="ts">
  import { type Ref, ref, watch } from 'vue'
  import * as turf from '@turf/turf'
  import Plotly from 'plotly.js-dist'
  import { useSectionLineVisualisationStore } from '@/stores/sectionVisualisation'
  import { debounce } from 'lodash'
  import { useEventsBus } from '@haskoning/vue-component-library'
  import type { GTLPSectionVisualisation } from '@/stores/type/sectionVisualisation'
  import type { Annotations, Layout, PlotlyHTMLElement } from 'plotly.js'
  import { storeToRefs } from 'pinia'
  import { useProjectStore } from '@/stores/project'
  import {
    calculateXAxisDtick,
    calculateYAxisDtick,
    createPlotlyGeoprofileVisualizationData,
    onLegendClicked,
    updateAnnotationsIfNeeded
  } from '@haskoning/geoprofile-visualization'

  const props = defineProps({
    showLegend: {
      type: Boolean,
      default: false
    }
  })
  const projectStore = useProjectStore()
  const { lengthProfileGraphBar: graphBarConfig } = storeToRefs(projectStore)

  const { bus } = useEventsBus()
  const emit = defineEmits(['set-all-traces', 'set-layout'])
  const sectionVisualisationStore = useSectionLineVisualisationStore()
  const sectionLinesStore = useSectionLinesStore()
  const { visualiseItem } = storeToRefs(sectionLinesStore)
  const { currentItem, yAxisRange, writeLoading } = storeToRefs(sectionVisualisationStore)
  const plotlyChart: Ref<PlotlyHTMLElement | null> = ref(null)
  const verticalExaggeration = ref(0)
  const isCurrentItemNotEmpty = computed(() => Object.keys(currentItem.value).length > 0)
  const isReady = ref(false)
  const rasterFileUploadStore = useRasterFileUploadStore()
  const giDataStore = useGIDataUploadCollectionStore()

  const resetChart = (width: number, height: number) => {
    if (plotlyChart.value && isReady.value) {
      Plotly.relayout(plotlyChart.value, {
        height: height,
        width: width
      })
    }
  }

  defineExpose({ resetChart })

  const createDebouncedRelayoutHandler = (originalAnnotationTexts: string[]) => {
    let internalUpdate = false // Flag to detect if the update is internal, Plotly.relayout seems to trigger a loop
    return debounce(function (eventData: any) {
      if (internalUpdate) {
        internalUpdate = false
        return
      }
      const yCurrentRange = eventData['yaxis.range[1]'] - eventData['yaxis.range[0]']
      const xCurrentRange = eventData['xaxis.range[1]'] - eventData['xaxis.range[0]']
      if (!isNaN(yCurrentRange)) {
        sectionVisualisationStore.setYAxisRange([
          eventData['yaxis.range[0]'],
          eventData['yaxis.range[1]']
        ])
      }
      const layoutUpdate: {
        annotations?: Partial<Annotations>[]
        'yaxis.dtick'?: number
        'xaxis.dtick'?: number
      } = {}
      const layout = plotlyChart.value?.layout
      if (layout?.annotations) {
        const updatedAnnotations = updateAnnotationsIfNeeded(
          { annotations: layout.annotations },
          yCurrentRange,
          originalAnnotationTexts
        )
        if (updatedAnnotations) {
          layoutUpdate.annotations = updatedAnnotations
        }
      }

      if (yCurrentRange && xCurrentRange) {
        layoutUpdate['yaxis.dtick'] = calculateYAxisDtick(yCurrentRange)
        layoutUpdate['xaxis.dtick'] = calculateXAxisDtick(xCurrentRange)
      }

      if (Object.keys(layoutUpdate).length > 0) {
        internalUpdate = true
        Plotly.relayout(plotlyChart.value, layoutUpdate)
      }
    }, 500)
  }

  watch(currentItem, (newVal: GTLPSectionVisualisation, oldVal: GTLPSectionVisualisation) => {
    if (newVal !== oldVal && plotlyChart.value) {
      const plotlyData = createPlotlyGeoprofileVisualizationData({
        geoprofileData: newVal,
        rasterMeta: rasterFileUploadStore.items,
        locationIdColorMapping: giDataStore.locationIdColorMapping,
        profileDistanceCorrection: sectionVisualisationStore.profileDistanceCorrection
      })
      if (!plotlyData) {
        return
      }
      const { dataTraces, layout, labelTraces } = plotlyData
      const debouncedRelayoutHandler = createDebouncedRelayoutHandler(
        labelTraces.map((trace) => trace.text) as string[]
      )

      Plotly.newPlot(plotlyChart.value, dataTraces, layout, {
        scrollZoom: true,
        modeBarButtonsToRemove: ['lasso', 'select']
      }).then(() => {
        isReady.value = true
        onLegendClicked(plotlyChart.value, dataTraces)
      })

      plotlyChart.value.on('plotly_relayout', debouncedRelayoutHandler)
      emit('set-all-traces', dataTraces)
      emit('set-layout', layout)
      Plotly.relayout(plotlyChart.value, {
        annotations: labelTraces,
        height: graphBarConfig.value.height
      })
      const plotlyLayout: Layout = plotlyChart.value.layout
      sectionVisualisationStore.setYAxisRange(plotlyLayout.yaxis.range as number[])
    }
  })

  watch(
    () => bus.value.get('mouse-position'),
    (coords) => {
      if (!visualiseItem.value?.geometry?.coordinates || !plotlyChart.value) return
      const pt = turf.point(coords[0])
      const startedPoint = turf.point(visualiseItem.value.geometry.coordinates[0])
      const distance = turf.distance(pt, startedPoint, { units: 'meters' })
      if (!plotlyChart.value.data) {
        return
      }
      const lastTrace = plotlyChart.value.data[plotlyChart.value.data.length - 1]
      if (lastTrace?.name === 'Indicator') {
        Plotly.deleteTraces(plotlyChart.value, plotlyChart.value.data.length - 1)
      }
      Plotly.addTraces(plotlyChart.value, {
        x: [distance, distance],
        y: [-20, 40],
        name: 'Indicator',
        mode: 'lines+text',
        line: {
          color: 'grey',
          dash: 'dash'
        }
      })
    }
  )

  watch(
    () => yAxisRange.value,
    (newVal) => {
      if (isCurrentItemNotEmpty.value && plotlyChart.value) {
        const plotlyLayout = plotlyChart.value.layout
        Plotly.relayout(plotlyChart.value, { 'yaxis.range': newVal })
        const xAxisRange = plotlyLayout.xaxis.range
          ? plotlyLayout.xaxis.range[1] - plotlyLayout.xaxis.range[0]
          : 0
        const yAxisRange = plotlyLayout.yaxis.range
          ? plotlyLayout.yaxis.range[1] - plotlyLayout.yaxis.range[0]
          : 0
        verticalExaggeration.value = xAxisRange / yAxisRange
      }
    }
  )

  watch(
    () => props.showLegend,
    (newVal) => {
      Plotly.relayout(plotlyChart.value, { showlegend: newVal })
    }
  )
</script>

<template>
  <div ref="plotlyChart">
    <v-tooltip text="Vertical Exaggeration">
      <template v-slot:activator="{ props }">
        <div
          v-bind="props"
          class="vertical-exaggeration"
          v-if="isCurrentItemNotEmpty && !writeLoading"
        >
          <strong>VE {{ verticalExaggeration.toFixed(2) }}</strong>
        </div>
      </template>
    </v-tooltip>
    <v-range-slider
      v-if="isCurrentItemNotEmpty && !writeLoading"
      class="range-slider"
      v-model="yAxisRange"
      direction="vertical"
      strict
      :max="currentItem.max_y_slider"
      :min="currentItem.min_y_slider"
      :step="1"
      thumb-label="always"
      @change="sectionVisualisationStore.setYAxisRange(yAxisRange)"
    ></v-range-slider>
  </div>
</template>
<style scoped>
  .vertical-exaggeration {
    position: absolute;
    bottom: 20px;
    right: 10%;

    color: #000;
    font-weight: 500;
    z-index: 100;
  }

  .range-slider {
    position: absolute;
    bottom: 0;
    right: 25px;
    z-index: 100;
  }

  :deep(.v-slider-thumb__label) {
    left: -60px !important;
  }

  :deep(.v-slider-thumb__label::before) {
    content: none;
  }
</style>
